import { cn } from '@folio/lib';
import { QuoteContext } from '@folio/modules/shared-quote/QuoteContext';
import type { TFunction } from 'i18next';
import type { ConfigQuoteChosenWarrantyDto } from 'korint-sdk';
import { CircleCheckBig, CircleX } from 'lucide-react';
import { useContext } from 'react';

export type FormulaGuaranteeLine = { key: string; label: string; active: boolean };

export function useQuotePerils(tier?: string): ConfigQuoteChosenWarrantyDto[] {
  const { calculatedQuotes } = useContext(QuoteContext);
  return (
    calculatedQuotes.find(quote => quote.tier === tier)?.quote?.assets?.[0]?.options.perils ?? []
  );
}

/** Label of a valued peril (LOI, Franchise…), with its selected option interpolated via the
 *  `{{value}}` plural keys. Falls back to the plain label when no option is selected. */
export function valuedPerilLabel(
  t: TFunction,
  prefix: string,
  perils: ConfigQuoteChosenWarrantyDto[],
  id: string,
): string {
  const rawValue = perils.find(peril => peril.id === id)?.option;
  if (rawValue === undefined) return t(`${prefix}.perils.${id}.label`);
  const value = t(`${prefix}.perils.${id}.options.${rawValue}`, {
    defaultValue: String(rawValue),
  });
  return t(`${prefix}.perils.${id}.label`, { value, count: Number(rawValue) || 0 });
}

export function isPerilActive(perils: ConfigQuoteChosenWarrantyDto[], id: string): boolean {
  const peril = perils.find(peril => peril.id === id);
  return Boolean(peril) && peril?.available !== false && !peril?.unavailabilityMessage;
}

export function FormulaGuaranteeLines({ lines }: { lines: FormulaGuaranteeLine[] }) {
  return (
    <ul className="flex flex-col gap-2">
      {lines.map(({ key, label, active }) => (
        <li
          key={key}
          className={cn('flex items-start gap-2 text-sm', !active && 'text-muted-foreground')}
        >
          {active ? (
            <CircleCheckBig className="h-4 w-4 shrink-0 mt-0.5 text-green-500" />
          ) : (
            <CircleX className="h-4 w-4 shrink-0 mt-0.5 text-red-500" />
          )}
          <span className={cn(!active && 'line-through')}>{label}</span>
        </li>
      ))}
    </ul>
  );
}
